import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, timeAgo } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { Avatar, ConfirmAction } from "@/components/common";
import { Button } from "@/components/ui/button";
import { ReactionBar, ReactionSummary } from "@/components/Reactions";
import { MentionInput, MentionText, mentionIds } from "@/components/Mentions";

// One comment, with its own reactions (same 5 as Facts and posts) and an optional Reply button.
export const CommentRow = ({ comment, onRemove, onReply, small = false }) => {
  const { me } = useMe();
  const [c, setC] = useState(comment);
  useEffect(() => { setC((x) => ({ ...x, reaction_count: comment.reaction_count, reaction_counts: comment.reaction_counts })); },
    [comment.reaction_count, JSON.stringify(comment.reaction_counts || {})]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex gap-2.5" data-testid={`comment-${c.id}`}>
      <Avatar user={c.author} size={small ? 24 : 28} />
      <div className="min-w-0 flex-1">
        <div className="inline-block max-w-full rounded-2xl bg-[#F5F1EB] px-3 py-2 text-sm"><span className="font-semibold">{c.author?.name}</span> <span className="break-words text-slate-700"><MentionText text={c.text} mentions={c.mentions} /></span></div>
        <div className="mt-0.5 flex flex-wrap items-center gap-1 text-slate-500">
          <span className="px-2 text-[11px]">{timeAgo(c.created_at)}</span>
          <ReactionBar small item={c} setItem={setC} url={`/comments/${c.id}/react`} whoUrl={`/comments/${c.id}/reactions`} testid={`comment-react-${c.id}`} />
          <ReactionSummary item={c} testid={`comment-react-${c.id}`} className="px-1 !text-[11px] !font-normal text-slate-500" />
          {onReply && <button type="button" onClick={() => onReply(c)} className="rounded-full px-2 py-0.5 text-xs font-medium transition-colors hover:bg-black/5" data-testid={`comment-reply-btn-${c.id}`}>Reply</button>}
          {onRemove && c.author_id === me?.user_id && <ConfirmAction testid={`comment-delete-${c.id}`} title="Delete this comment?" description={c.parent_id ? "It will be permanently deleted for everyone." : "It will be permanently deleted for everyone, with any replies. Nobody, including Middle, can restore it."} confirmLabel="Delete" destructive onConfirm={() => onRemove(c.id)}
            trigger={<button className="rounded-full p-1 text-slate-400 hover:text-rose-600" aria-label="Delete comment" data-testid={`comment-delete-btn-${c.id}`}><Trash2 size={13} /></button>} />}
        </div>
      </div>
    </div>
  );
};

const Composer = ({ id, onSend, placeholder, autoFocus, onCancel }) => {
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const send = async () => { if (text.trim() && await onSend(text, mentionIds(text, mentions))) { setText(""); setMentions([]); } };
  return (
    <div className="flex gap-2">
      <MentionInput testid={`comment-input-${id}`} value={text} onChange={setText} mentions={mentions} setMentions={setMentions} autoFocus={autoFocus}
        onKeyDown={(e) => { if (e.key === "Enter") send(); if (e.key === "Escape") onCancel?.(); }}
        placeholder={placeholder} className="w-full rounded-full border border-border bg-white px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-200" />
      <Button size="sm" className="rounded-full" data-testid={`comment-submit-${id}`} onClick={send}>{onCancel ? "Reply" : "Post"}</Button>
    </div>
  );
};

// Comments on a member post (base "/posts/<id>"), a checked Fact ("/facts/<id>") or a Middle Live issue ("/live/<id>").
// Replies sit one level under their comment.
export const Comments = ({ base, id, onCount, className = "mt-3 border-t border-border pt-3" }) => {
  const [items, setItems] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  useEffect(() => { api.get(`${base}/comments`).then((r) => setItems(r.data)).catch(() => setItems([])); }, [base]);
  const remove = async (cid) => {
    try {
      const { data } = await api.delete(`/comments/${cid}`);
      setItems((xs) => xs.filter((c) => c.id !== cid && c.parent_id !== cid)); onCount(-(data.removed || 1)); toast.success("Comment permanently deleted");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const add = async (text, parentId, mentions = []) => {
    try {
      const { data } = await api.post(`${base}/comments`, { text, parent_id: parentId || undefined, mentions });
      setItems((xs) => [...(xs || []), data]); onCount(1); if (parentId) setReplyTo(null);
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const ids = new Set((items || []).map((c) => c.id));
  const top = (items || []).filter((c) => !c.parent_id || !ids.has(c.parent_id));
  const repliesOf = (pid) => (items || []).filter((c) => c.parent_id === pid);
  const reply = (c) => setReplyTo({ id: c.parent_id && ids.has(c.parent_id) ? c.parent_id : c.id, name: c.author?.name?.split(" ")[0] });
  return (
    <div className={`space-y-3 ${className}`} data-testid={`comments-${id}`}>
      {top.map((c) => (
        <div key={c.id} className="space-y-2">
          <CommentRow comment={c} onRemove={remove} onReply={reply} />
          {(repliesOf(c.id).length > 0 || replyTo?.id === c.id) && (
            <div className="ml-9 space-y-2 border-l-2 border-border pl-3" data-testid={`comment-replies-${c.id}`}>
              {repliesOf(c.id).map((r) => <CommentRow key={r.id} comment={r} onRemove={remove} onReply={reply} small />)}
              {replyTo?.id === c.id && <Composer id={`reply-${c.id}`} autoFocus placeholder={`Reply to ${replyTo.name || "this comment"}…`} onSend={(t, m) => add(t, c.id, m)} onCancel={() => setReplyTo(null)} />}
            </div>)}
        </div>
      ))}
      {items?.length === 0 && <p className="text-xs text-slate-400" data-testid={`comments-empty-${id}`}>No comments yet.</p>}
      <Composer id={id} placeholder="Write a kind comment… Type @ to mention someone" onSend={(t, m) => add(t, null, m)} />
    </div>
  );
};
